import type { ExtractorGroup, MessageInfo } from '../../entities/message/messageInfoTypes';
import { mockMessages } from './messageMocks';
import { MOCK_GROUP_ID } from './mockSite';

const audit = { companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema', createDate: '2026-03-10T10:00:00Z', modifiedDate: '2026-03-10T10:00:00Z' };

/**
 * Template MessageInfo allineato alle chiavi degli extractor reali backend.
 *
 * Lato server, `DTOConverterUtil.toMessage` popola `Message.extractorInfos[]`
 * filtrando i MessageInfo per `label IN extractor.getKeys()`. Le chiavi qui
 * sono l'unione di:
 *   - BusinessDataMessageExtractor:   {companyName, companyEmail, companyTelephone,
 *                                       companyVatNumber, companyDomain, businessExists}
 *   - TechnicalDataMessageExtractor:  {isNew, accessType, siteAddress, gMap}
 *   - LoadBearingMessageExtractor:    {material, materialType, joistSize, beamSize}
 *   - CoverMessageExtractor:          {width, joistSpacing, photovoltaic,
 *                                       solarThermal, plannedInstallation}
 *   - CoverSecurityMessageExtractor:  {obstacles, securityLevel, laying,
 *                                       technicalReport}
 *
 * Convenzione split estratte/mancanti (vedi MessageInbox `splitInfos`):
 *   value popolato  -> "Informazioni estratte"
 *   value vuoto     -> "Informazioni mancanti"
 *
 * `businessExists` è speciale: value 'true'/'false' + classPK = Business.id
 * quando match (UI può linkare al record), 0 altrimenti. Coerente con
 * BusinessDataMessageExtractor.extract.
 *
 * Per i messaggi OUT (Jader Francia in uscita) nessun extractor SYSTEM viene
 * eseguito (BusinessData/TechnicalData filtrano `IN` in getDirections), quindi
 * non viene prodotto alcun MessageInfo.
 */
const LABEL_TEMPLATE = [
  { label: 'companyName',      labelTranslated: 'Nome azienda',       className: '' },
  { label: 'companyEmail',     labelTranslated: 'Email aziendale',    className: '' },
  { label: 'companyTelephone', labelTranslated: 'Telefono aziendale', className: '' },
  { label: 'companyVatNumber', labelTranslated: 'Partita IVA',        className: '' },
  { label: 'companyDomain',    labelTranslated: 'Dominio web',        className: '' },
  { label: 'businessExists',   labelTranslated: 'Azienda a sistema',  className: 'it.dvel.crm.core.model.Business' },
  { label: 'isNew',            labelTranslated: 'Edificio nuovo',     className: '' },
  { label: 'accessType',       labelTranslated: 'Tipo accesso',       className: '' },
  { label: 'siteAddress',      labelTranslated: 'Indirizzo cantiere', className: '' },
  { label: 'gMap',             labelTranslated: 'Google Maps',        className: '' },
  { label: 'material',         labelTranslated: 'Materiale struttura', className: '' },
  { label: 'materialType',     labelTranslated: 'Sotto-tipo materiale', className: '' },
  { label: 'joistSize',        labelTranslated: 'Sezione travetti (cm)', className: '' },
  { label: 'beamSize',         labelTranslated: 'Sezione trave (cm)',    className: '' },
  { label: 'width',            labelTranslated: 'Spessore copertura (cm)', className: '' },
  { label: 'joistSpacing',     labelTranslated: 'Interasse travetti (cm)', className: '' },
  { label: 'photovoltaic',     labelTranslated: 'Fotovoltaico esistente', className: '' },
  { label: 'solarThermal',     labelTranslated: 'Solare termico esistente', className: '' },
  { label: 'plannedInstallation', labelTranslated: 'Installazione futura prevista', className: '' },
  { label: 'obstacles',        labelTranslated: 'Ostacoli sul sito',        className: '' },
  { label: 'securityLevel',    labelTranslated: 'Livello sicurezza',        className: '' },
  { label: 'laying',           labelTranslated: 'Includere posa in opera',  className: '' },
  { label: 'technicalReport',  labelTranslated: 'Includere documentazione tecnica', className: '' },
] as const;

/**
 * Label "false-by-default": se il seed non fornisce value, makeInfos persiste
 * "false" invece di "". Riflette il comportamento degli extractor backend che
 * trattano l'assenza di info come "no, il cliente non sta chiedendo quello".
 * Vedi Cover.plannedInstallation e CoverSecurity.{obstacles,laying,technicalReport}.
 */
const FALSE_DEFAULT_LABELS: ReadonlySet<string> = new Set([
  'plannedInstallation',
  'obstacles',
  'laying',
  'technicalReport',
]);

/**
 * Mapping label -> FQN dell'extractor che la produce. Usato da
 * {@link groupMockInfosByExtractor} per replicare lato mock il raggruppamento
 * che il backend (DTOConverterUtil.toMessage) fa per `label IN
 * extractor.getKeys()`. Manteniamo la lista allineata alle key degli extractor
 * reali quando se ne aggiunge/rinomina uno.
 */
const LABEL_TO_FQN: Record<string, string> = {
  companyName: 'it.dvel.crm.core.internal.mail.extractor.BusinessDataMessageExtractor',
  companyEmail: 'it.dvel.crm.core.internal.mail.extractor.BusinessDataMessageExtractor',
  companyTelephone: 'it.dvel.crm.core.internal.mail.extractor.BusinessDataMessageExtractor',
  companyVatNumber: 'it.dvel.crm.core.internal.mail.extractor.BusinessDataMessageExtractor',
  companyDomain: 'it.dvel.crm.core.internal.mail.extractor.BusinessDataMessageExtractor',
  businessExists: 'it.dvel.crm.core.internal.mail.extractor.BusinessDataMessageExtractor',
  isNew: 'it.dvel.crm.ts.internal.mail.extractor.TechnicalDataMessageExtractor',
  accessType: 'it.dvel.crm.ts.internal.mail.extractor.TechnicalDataMessageExtractor',
  siteAddress: 'it.dvel.crm.ts.internal.mail.extractor.TechnicalDataMessageExtractor',
  gMap: 'it.dvel.crm.ts.internal.mail.extractor.TechnicalDataMessageExtractor',
  material: 'it.dvel.crm.ts.internal.mail.extractor.LoadBearingMessageExtractor',
  materialType: 'it.dvel.crm.ts.internal.mail.extractor.LoadBearingMessageExtractor',
  joistSize: 'it.dvel.crm.ts.internal.mail.extractor.LoadBearingMessageExtractor',
  beamSize: 'it.dvel.crm.ts.internal.mail.extractor.LoadBearingMessageExtractor',
  width: 'it.dvel.crm.ts.internal.mail.extractor.CoverMessageExtractor',
  joistSpacing: 'it.dvel.crm.ts.internal.mail.extractor.CoverMessageExtractor',
  photovoltaic: 'it.dvel.crm.ts.internal.mail.extractor.CoverMessageExtractor',
  solarThermal: 'it.dvel.crm.ts.internal.mail.extractor.CoverMessageExtractor',
  plannedInstallation: 'it.dvel.crm.ts.internal.mail.extractor.CoverMessageExtractor',
  obstacles: 'it.dvel.crm.ts.internal.mail.extractor.CoverSecurityMessageExtractor',
  securityLevel: 'it.dvel.crm.ts.internal.mail.extractor.CoverSecurityMessageExtractor',
  laying: 'it.dvel.crm.ts.internal.mail.extractor.CoverSecurityMessageExtractor',
  technicalReport: 'it.dvel.crm.ts.internal.mail.extractor.CoverSecurityMessageExtractor',
};

/**
 * Priority OSGi degli extractor, replica delle property `priority` dichiarate
 * nei @Component Java (es. OSGiPropertyConstants.PROP_PRIORITY + "=20"). Lato
 * REST reale e' il backend a ordinare i gruppi ExtractorGroup per questa
 * priority; nei mock la usiamo in {@link groupMockInfosByExtractor} per dare lo
 * stesso ordinamento dei gruppi. Tutti gli extractor attuali hanno priority=20
 * (default in @Component): se in futuro qualcuno avra' priority diversa,
 * aggiornare qui.
 */
const FQN_TO_PRIORITY: Record<string, number> = {
  'it.dvel.crm.core.internal.mail.extractor.BusinessDataMessageExtractor': 20,
  'it.dvel.crm.ts.internal.mail.extractor.TechnicalDataMessageExtractor': 20,
  'it.dvel.crm.ts.internal.mail.extractor.LoadBearingMessageExtractor': 20,
  'it.dvel.crm.ts.internal.mail.extractor.CoverMessageExtractor': 20,
  'it.dvel.crm.ts.internal.mail.extractor.CoverSecurityMessageExtractor': 20,
};

type TemplateLabel = typeof LABEL_TEMPLATE[number]['label'];
type InfoSeed = { value: string; classPK?: number };
type InfoSeeds = Partial<Record<TemplateLabel, InfoSeed>>;

/**
 * Genera MessageInfo per un messaggio applicando il template.
 *  - Per direction IN: tutte le label del template (BusinessData + Technical girano solo IN).
 *  - Per direction OUT: nessuna label (tutti gli extractor hanno
 *    getDirections() = {IN} → dispatcher li skippa).
 *
 * Chiavi senza seed restano value vuoto (= "Informazioni mancanti" UI).
 * messageInfoId = messageId * 100 + priority + 1 (priority = idx in LABEL_TEMPLATE).
 */
function makeInfos(messageId: number, seeds: InfoSeeds, direction: 'IN' | 'OUT' = 'IN'): MessageInfo[] {
  const labels = direction === 'OUT'
    ? []
    : LABEL_TEMPLATE;
  const received = mockMessages.find((m) => m.messageId === messageId)?.createDate ?? audit.createDate;
  return labels.map((def, idx) => {
    const seed = seeds[def.label];
    const hasRef = seed?.classPK != null && def.className !== '';
    // Default semantico "false" per le label dichiarate "MAI null" dagli extractor
    // (vedi FALSE_DEFAULT_LABELS sopra). Le altre label hanno default ""
    // (= "Informazioni mancanti" UI).
    const defaultValue = FALSE_DEFAULT_LABELS.has(def.label) ? 'false' : '';
    return {
      messageInfoId: messageId * 100 + idx + 1,
      messageId,
      label: def.label,
      labelTranslated: def.labelTranslated,
      value: seed?.value ?? defaultValue,
      className: hasRef ? def.className : '',
      classPK: hasRef ? seed!.classPK! : 0,
      priority: idx,
      ...audit,
      // Un'info nasce con il suo messaggio: mai prima della sua createDate.
      ...(received > audit.createDate && { createDate: received, modifiedDate: received }),
    };
  });
}

/**
 * Raggruppa le info mock per extractor di provenienza, replicando lato mock
 * cio' che il backend (DTOConverterUtil.toMessage) fa server-side dopo TS-10:
 * `Message.extractorInfos` arriva gia' come ExtractorGroup[] ordinato per
 * priority dell'extractor. Usa {@link LABEL_TO_FQN} per il className del gruppo
 * e {@link FQN_TO_PRIORITY} per l'ordine dei gruppi (tie-break alfabetico sul
 * FQN). Le info la cui label non e' mappata a un extractor noto sono escluse
 * (coerente col filtro server per label IN extractor.getKeys()). Dentro ogni
 * gruppo le info restano ordinate per `priority` ascendente.
 */
export function groupMockInfosByExtractor(infos: MessageInfo[]): ExtractorGroup[] {
  const byFqn = new Map<string, MessageInfo[]>();
  for (const info of infos) {
    const fqn = LABEL_TO_FQN[info.label];
    if (!fqn) continue;
    const list = byFqn.get(fqn) ?? [];
    list.push(info);
    byFqn.set(fqn, list);
  }
  return Array.from(byFqn.entries())
    .sort(([fqnA], [fqnB]) => {
      const pa = FQN_TO_PRIORITY[fqnA] ?? 0;
      const pb = FQN_TO_PRIORITY[fqnB] ?? 0;
      if (pa !== pb) return pa - pb;
      return fqnA.localeCompare(fqnB);
    })
    .map(([className, groupInfos]) => ({
      className,
      infos: [...groupInfos].sort((a, b) => a.priority - b.priority),
    }));
}

export const mockMessageInfos: MessageInfo[] = [
  // 1 — Alice Smith (Acme Corp via classNameId in Relationship, ma dominio example.com generico → no match domain)
  ...makeInfos(1, {
    'businessExists': { value: 'false' },
    'isNew': { value: 'true' },
  }),

  // 2 — Bob Johnson @ acmecorp.com → match Acme Corp (businessId 3)
  ...makeInfos(2, {
    'companyName': { value: 'Acme Corp' },
    'companyDomain': { value: 'acmecorp.com' },
    'businessExists': { value: 'true', classPK: 3 },
  }),

  // 3 — Charlie Brown @ fornari.it (dominio diverso da aziendaagricolafornari.it → no match)
  ...makeInfos(3, {
    'companyDomain': { value: 'fornari.it' },
    'businessExists': { value: 'false' },
  }),

  // 4 — Diana Prince spam SEO, dominio marketing.com
  ...makeInfos(4, {
    'companyDomain': { value: 'marketing.com' },
    'businessExists': { value: 'false' },
  }),

  // 5 — Eve Adams @ miksi.it → match Miksi Spa (businessId 4)
  ...makeInfos(5, {
    'companyName': { value: 'Miksi' },
    'companyDomain': { value: 'miksi.it' },
    'businessExists': { value: 'true', classPK: 4 },
  }),

  // 6 — Luca Moretti @ costruzionivalle.it → match Costruzioni Valle (10), cantiere Brescia
  ...makeInfos(6, {
    'companyName': { value: 'Costruzioni Valle Spa' },
    'companyDomain': { value: 'costruzionivalle.it' },
    'businessExists': { value: 'true', classPK: 10 },
    'isNew': { value: 'true' },
    'siteAddress': { value: 'Via Brescia 200, Brescia' },
  }),

  // 7 — Marta Fabbri @ gmail.com (dominio generico escluso)
  ...makeInfos(7, {
    'businessExists': { value: 'false' },
  }),

  // 8 — Thomas Huber @ alpisolar.at (email in tedesco) → match AlpiSolar GmbH (9)
  ...makeInfos(8, {
    'companyName': { value: 'AlpiSolar GmbH' },
    'companyDomain': { value: 'alpisolar.at' },
    'businessExists': { value: 'true', classPK: 9 },
  }),

  // 9 — Davide Pellegrini @ euroimpianti.it → match EuroImpianti (12); impianto IMP-2025-0847 Bolzano in garanzia
  ...makeInfos(9, {
    'companyName': { value: 'EuroImpianti Srl' },
    'companyDomain': { value: 'euroimpianti.it' },
    'businessExists': { value: 'true', classPK: 12 },
    'isNew': { value: 'false' },
    'siteAddress': { value: 'Bolzano (stabilimento)' },
  }),

  // 10 — Elena Marchetti @ giubra.com → match Giubra Srl (5)
  ...makeInfos(10, {
    'companyName': { value: 'Giubra Srl' },
    'companyDomain': { value: 'giubra.com' },
    'businessExists': { value: 'true', classPK: 5 },
  }),

  // 11 — OUT: Jader Francia (D'vel) → Eugenio Fornari. Nessun MessageInfo (BusinessData/Technical skip OUT).
  ...makeInfos(11, {
  }, 'OUT'),

  // 12 — Giorgio Visconti @ technovent.it → match TechnoVent (8)
  ...makeInfos(12, {
    'companyName': { value: 'TechnoVent Srl' },
    'companyDomain': { value: 'technovent.it' },
    'businessExists': { value: 'true', classPK: 8 },
  }),

  // 13 — Anna De Luca @ outlook.com (dominio generico escluso) — privato B2C
  ...makeInfos(13, {
    'businessExists': { value: 'false' },
  }),

  // 14 — Marco Zanetti @ trentinosicurezza.it → match Trentino Sicurezza (6)
  ...makeInfos(14, {
    'companyName': { value: 'Trentino Sicurezza Srl' },
    'companyDomain': { value: 'trentinosicurezza.it' },
    'businessExists': { value: 'true', classPK: 6 },
  }),

  // 15 — OUT: Jader → Claudia Rizzo, cemento Sesto San Giovanni
  ...makeInfos(15, {
  }, 'OUT'),

  // 16 — OUT: Jader → Roberto Bianchi, conferma ordine Z3
  ...makeInfos(16, {
  }, 'OUT'),

  // 17 — Giulia Ferri (personId 9), Edilizia Moderna Spa (businessId 7) → TUTTE le info popolate
  ...makeInfos(17, {
    'companyName': { value: 'Edilizia Moderna Spa' },
    'companyEmail': { value: 'info@edilizia-moderna.it' },
    'companyTelephone': { value: '+39 0464 223300' },
    'companyVatNumber': { value: 'IT01234567890' },
    'companyDomain': { value: 'edilizia-moderna.it' },
    'businessExists': { value: 'true', classPK: 7 },
    'isNew': { value: 'true' },
    'accessType': { value: 'PLATFORM' },
    'siteAddress': { value: 'Via dei Pioppi 12, 38068 Rovereto (TN)' },
    'gMap': { value: 'https://maps.google.com/?q=45.8908,11.0411' },
  }),

  // 18 — Davide Costa @ calcestruzziveneti.it (no match) — palazzo storico, accesso scale
  ...makeInfos(18, {
    'companyName': { value: 'Calcestruzzi Veneti Srl' },
    'companyDomain': { value: 'calcestruzziveneti.it' },
    'businessExists': { value: 'false' },
    'isNew': { value: 'false' },
    'accessType': { value: 'STAIRS' },
  }),

  // 19 — Marco Zanetti (personId 13), Trentino Sicurezza (businessId 6) — solo siteAddress lato Technical
  ...makeInfos(19, {
    'companyName': { value: 'Trentino Sicurezza Srl' },
    'companyDomain': { value: 'trentinosicurezza.it' },
    'businessExists': { value: 'true', classPK: 6 },
    'siteAddress': { value: 'Via della Sicurezza 12, 38121 Trento' },
  }),

  // 20 — Francesca Romano @ gmail (caso degenere, info quasi nulle)
  ...makeInfos(20, {
    'businessExists': { value: 'false' },
  }),

  // 21 — Federico Olivieri (Studio Olivieri Architetti, no match) — LoadBearing WOOD
  ...makeInfos(21, {
    'companyName': { value: 'Studio Olivieri Architetti' },
    'companyDomain': { value: 'olivieri-architetti.it' },
    'businessExists': { value: 'false' },
    'isNew': { value: 'false' },
    'accessType': { value: 'STAIRS' },
    'siteAddress': { value: 'Via Roma 45, 38057 Pergine Valsugana (TN)' },
    'gMap': { value: 'https://maps.google.com/?q=46.0623,11.2370' },
    'material': { value: 'WOOD' },
    // materialType omesso: LoadBearing non lo emette per material != METAL
    // TS-136 — sezioni dette nella mail: precompilano il popup «Struttura e posa».
    'joistSize': { value: '10x12' },
    'beamSize': { value: '12x16' },
  }),

  // 22 — Luca Moretti (personId 10), Costruzioni Valle Spa (businessId 10) — LoadBearing METAL+CORRUGATED
  ...makeInfos(22, {
    'companyName': { value: 'Costruzioni Valle Spa' },
    'companyTelephone': { value: '+39 030 5566778' },
    'companyDomain': { value: 'costruzionivalle.it' },
    'businessExists': { value: 'true', classPK: 10 },
    'isNew': { value: 'true' },
    'accessType': { value: 'PLATFORM' },
    'siteAddress': { value: 'Via dell\'Industria 88, 20099 Sesto San Giovanni (MI)' },
    'material': { value: 'METAL' },
    'materialType': { value: 'CORRUGATED' },
  }),

  // 23 — Sergio Brambilla (privato gmail, no match) — LoadBearing METAL+STANDING_SEAM
  ...makeInfos(23, {
    'businessExists': { value: 'false' },
    'isNew': { value: 'true' },
    'accessType': { value: 'WINDOW' },
    'siteAddress': { value: 'Via del Lago 12, 23900 Lecco' },
    'gMap': { value: 'https://maps.google.com/?q=45.8550,9.3970' },
    'material': { value: 'METAL' },
    'materialType': { value: 'STANDING_SEAM' },
  }),

  // 24 — Studio Archirossi (no match) — Cover: photovoltaic già installato + plannedInstallation linea vita
  ...makeInfos(24, {
    'companyName': { value: 'Studio Archirossi' },
    'companyDomain': { value: 'studio-archirossi.it' },
    'businessExists': { value: 'false' },
    'isNew': { value: 'false' },
    'accessType': { value: 'STAIRS' },
    'siteAddress': { value: 'Via Mazzini 30, 25121 Brescia' },
    'material': { value: 'METAL' },
    'width': { value: '8' },
    'joistSpacing': { value: '60' },
    'photovoltaic': { value: 'true' },
    'plannedInstallation': { value: 'true' },
  }),

  // 25 — Logistica Padova (no match) — Cover: industriale grande, nessun impianto, planned fotovoltaico + linea vita
  ...makeInfos(25, {
    'companyName': { value: 'Logistica Padova Srl' },
    'companyDomain': { value: 'logistica-padova.it' },
    'businessExists': { value: 'false' },
    'isNew': { value: 'false' },
    'siteAddress': { value: 'Via dell\'Interporto 22, 35127 Padova' },
    'material': { value: 'METAL' },
    'materialType': { value: 'CORRUGATED' },
    'width': { value: '4' },
    'joistSpacing': { value: '150' },
    'photovoltaic': { value: 'false' },
    'solarThermal': { value: 'false' },
    'plannedInstallation': { value: 'true' },
  }),

  // 26 — Anna Bianchi (privato hotmail, no match) — Cover: casa anni '50, info parziali, no impianti, no planned
  ...makeInfos(26, {
    'businessExists': { value: 'false' },
    'isNew': { value: 'false' },
    'siteAddress': { value: 'Via Roero 8, 12060 La Morra (CN)' },
    'material': { value: 'WOOD' },
    'width': { value: '12' },
    // joistSpacing/photovoltaic/solarThermal omessi → "Informazioni mancanti" UI
    // plannedInstallation eredita default 'false' da makeInfos (sopralluogo solo manutenzione)
  }),

  // 27 — Studio MB Ingegneria (no match) — CoverSecurity: ostacoli + TOTAL + posa + relazione tecnica
  ...makeInfos(27, {
    'companyName': { value: 'Studio MB Ingegneria' },
    'companyDomain': { value: 'studiomb-ing.it' },
    'businessExists': { value: 'false' },
    'isNew': { value: 'false' },
    'siteAddress': { value: 'Piazza Erbe 14, 37121 Verona' },
    'obstacles': { value: 'true' },
    'securityLevel': { value: 'TOTAL' },
    'laying': { value: 'true' },
    'technicalReport': { value: 'true' },
  }),

  // 28 — Ricci Costruzioni (no match) — CoverSecurity: sito sgombro + PARTIAL + solo fornitura + no perizia
  ...makeInfos(28, {
    'companyName': { value: 'Ricci Costruzioni Spa' },
    'companyDomain': { value: 'ricciconstruzioni.it' },
    'businessExists': { value: 'false' },
    'isNew': { value: 'true' },
    'siteAddress': { value: 'Via Industriale Ovest 12, 25028 Verolanuova (BS)' },
    'material': { value: 'METAL' },
    'materialType': { value: 'CORRUGATED' },
    'obstacles': { value: 'false' },
    'securityLevel': { value: 'PARTIAL' },
    'laying': { value: 'false' },
    'technicalReport': { value: 'false' },
  }),

  // 29 — Verdi Ediltermica (no match) — CoverSecurity: chiavi-in-mano (laying+technicalReport true), copertura vaga (securityLevel null)
  ...makeInfos(29, {
    'companyName': { value: 'Verdi Ediltermica' },
    'companyDomain': { value: 'verdi-ediltermica.it' },
    'businessExists': { value: 'false' },
    'siteAddress': { value: 'Via Castellana 200, 30174 Mestre (VE)' },
    'laying': { value: 'true' },
    'technicalReport': { value: 'true' },
    // obstacles default 'false' (info non disponibile, non menzionati)
    // securityLevel default '' (tri-state, null = non determinabile)
  }),
];

/**
 * MessageInfo "predefinite" (campo Message.infos lato backend): metadati di
 * sistema READ-ONLY, con label dell'enum MessageInfoLabel (es. headers).
 * Sono distinte dalle info degli extractor (mockMessageInfos -> extractorInfos),
 * che invece sono editabili. Set minimo a scopo dimostrativo della sezione
 * read-only; in real-REST le popola il server (DTOConverterUtil.toMessage).
 * messageInfoId in range dedicato (messageId*100 + 90) per non collidere con
 * le info degli extractor (messageId*100 + 1..11).
 */
export const mockPredefinedMessageInfos: MessageInfo[] = [
  {
    messageInfoId: 190, messageId: 1, label: 'headers', labelTranslated: 'Header',
    value: 'SPF=pass; DKIM=pass; spam-score=0.1', className: '', classPK: 0, priority: 0,
    ...audit,
  },
  {
    messageInfoId: 990, messageId: 9, label: 'headers', labelTranslated: 'Header',
    value: 'SPF=pass; DKIM=fail; spam-score=2.3', className: '', classPK: 0, priority: 0,
    ...audit,
  },
];
